import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Input } from '../../components/ui/input';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hotel-reserve-57/artifacts/fb0g6q0u_logo-yeni-amorium-copy.png';
const HOTEL_IMAGE = 'https://customer-assets.emergentagent.com/job_hotel-reserve-57/artifacts/4vrwgswc_iesdkwukaqi2q3ionehj.jpg';

export default function AdminLogin() {
  const { adminLogin, language } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(language === 'tr' ? 'Lütfen tüm alanları doldurun' : 'Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success(language === 'tr' ? 'Giriş başarılı!' : 'Login successful!');
      navigate('/admin/dashboard');
    } catch (err) {
      const msg = err.response?.data?.detail || (language === 'tr' ? 'Giriş başarısız' : 'Login failed');
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: '#F6F1E7', fontFamily: 'Manrope, sans-serif' }}>
      {/* Left: Form */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '3rem 4rem', maxWidth: '520px' }}>
        <div style={{ marginBottom: '2.5rem' }}>
          <img src={LOGO_URL} alt="Amorium Hotel" style={{ height: '48px', width: 'auto', objectFit: 'contain', marginBottom: '1.5rem', display: 'block' }} />
          <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: '32px', fontWeight: 600, color: '#1F3A2E', marginBottom: '6px' }}>
            {language === 'tr' ? 'Hoş Geldiniz' : 'Welcome Back'}
          </h1>
          <p style={{ fontSize: '14px', color: '#6E7F5B' }}>
            {language === 'tr' ? 'Yönetim paneline erişmek için giriş yapın.' : 'Sign in to access the management panel.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '6px' }}>
              {language === 'tr' ? 'E-posta' : 'Email'}
            </label>
            <Input
              data-testid="admin-login-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="info@amoriumhotel.com"
              style={{ border: '1px solid #D8D1C6', borderRadius: '10px', padding: '12px 14px', fontSize: '14px', width: '100%', outline: 'none', fontFamily: 'Manrope, sans-serif' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '6px' }}>
              {language === 'tr' ? 'Şifre' : 'Password'}
            </label>
            <div style={{ position: 'relative' }}>
              <Input
                data-testid="admin-login-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••••"
                style={{ border: '1px solid #D8D1C6', borderRadius: '10px', padding: '12px 40px 12px 14px', fontSize: '14px', width: '100%', outline: 'none', fontFamily: 'Manrope, sans-serif' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#B0A99A', display: 'flex', alignItems: 'center' }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            style={{
              width: '100%', background: loading ? '#3E5A3F' : '#1F3A2E', color: 'white',
              fontSize: '14px', fontWeight: 700, padding: '14px', borderRadius: '10px',
              border: 'none', cursor: loading ? 'not-allowed' : 'pointer', marginTop: '4px',
              fontFamily: 'Manrope, sans-serif', transition: 'background 0.2s',
            }}
          >
            {loading ? (language === 'tr' ? 'Giriş yapılıyor...' : 'Signing in...') : (language === 'tr' ? 'Giriş Yap' : 'Sign In')}
          </button>
        </form>

      </div>

      {/* Right: Image (hidden on mobile) */}
      <div style={{ flex: 1, position: 'relative', display: window.innerWidth >= 1024 ? 'block' : 'none' }}>
        <img src={HOTEL_IMAGE} alt="Amorium Hotel" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(31,58,46,0.45)' }} />
        <div style={{ position: 'absolute', bottom: '48px', left: '48px', right: '48px', color: 'white' }}>
          <div style={{ width: '32px', height: '2px', background: '#B08D57', marginBottom: '12px' }} />
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '24px', fontWeight: 600, marginBottom: '4px' }}>Amorium Hotel</p>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)' }}>
            {language === 'tr' ? 'Doğanın kalbinde lüks bir kaçış' : 'A luxurious escape in the heart of nature'}
          </p>
        </div>
      </div>
    </div>
  );
}
